import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection } from '../components/sections/ContentSection';
import { Quote, ArrowRight, Loader2 } from 'lucide-react';
import { usePageData } from '../hooks/usePageData';
import { SEO } from '../components/SEO';
import { sanitizeHTML } from '../utils/sanitize';

export default function PastoralMessage() {
  const { pageData, loading, error } = usePageData('pastoral-message');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-slate-500">Failed to load page content</p>
        </div>
      </Layout>
    );
  }

  const { hero, sections, cta, seo } = pageData;
  const pastor = sections?.pastor || {};
  const quote = sections?.quote || "";
  const message = sections?.message || "";

  return (
    <Layout>
      <SEO 
        title={seo?.title || hero?.title}
        description={seo?.description || hero?.subtitle}
        image={seo?.image || hero?.image}
        url="/pastoral-message"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={hero?.image}
            alt={hero?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="pastoral-page-title">
            {hero?.title}
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto">
            {hero?.subtitle}
          </p>
        </div>
      </section>

      {/* Message Content */}
      <ContentSection bgColor="bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Pastor Image */}
            <div className="md:col-span-1">
              <div className="sticky top-28">
                <img 
                  src={pastor.image || hero?.image}
                  alt={pastor.name}
                  className="rounded-2xl shadow-lg w-full mb-4"
                />
                <h3 className="font-heading text-xl font-semibold text-primary-900">{pastor.name}</h3>
                <p className="text-slate-600 text-sm">{pastor.role}</p>
              </div>
            </div>

            {/* Message */}
            <div className="md:col-span-2">
              {/* Quote */}
              {quote && (
                <div className="bg-slate-50 rounded-xl p-8 mb-8 border-l-4 border-secondary">
                  <Quote className="w-10 h-10 text-secondary mb-4" />
                  <p className="text-xl text-primary-900 font-heading italic leading-relaxed">
                    "{quote}"
                  </p>
                </div>
              )}

              <div className="prose prose-lg max-w-none">
                <div 
                  className="text-slate-600 leading-relaxed space-y-6 [&>p]:mb-6"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }}
                />
                <p className="font-heading text-xl text-primary-900 font-semibold mt-8">
                  {pastor.name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* CTA */}
      {cta?.show && (
        <ContentSection bgColor={cta.bgColor || "bg-slate-50"} paddingY="py-16">
          <div className="text-center">
            <h2 className={`font-heading text-2xl md:text-3xl font-semibold mb-4 ${cta.bgColor === 'bg-primary-900' ? 'text-white' : 'text-primary-900'}`}>
              {cta.title}
            </h2>
            <p className={`mb-8 max-w-xl mx-auto ${cta.bgColor === 'bg-primary-900' ? 'text-slate-300' : 'text-slate-600'}`}>
              {cta.description}
            </p>
            <Link
              to={cta.buttonLink || "/contact"}
              className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
              data-testid="pastoral-contact-btn"
            >
              {cta.buttonText || "Get in Touch"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
